import React, { ReactNode } from 'react';
import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  pointerWithin,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { useTranslation } from 'react-i18next';
import { EmployeesService, EnrichedEventData, OrderData, OrderPageTypes } from '../../../api';
import { UnifiedShiftAndUserAvailability } from '../../UnifiedShiftAndUserAvailability';
import { useCalendar } from '../../CalendarContext/CalendarContext';
import { useOrders } from '../../swr/useOrders';
import { useEvents } from '../../swr/useEvents';
import { useAssignmentConflictHandler } from '../../../common/apiNotifications/responseHandlers/useAssignmentConflictHandler';
import { isEmployeeDropTarget, isResourceDropTarget, isUnassignedDropTarget, parseDropTargetId } from './DragDropTypes';
import { useHandleResourceDrop } from './useHandleResourceDrop';

interface DragDropProviderProps {
  children: ReactNode;
  orders: OrderData[];
  events: EnrichedEventData[];
  shiftAndAvailabilities: UnifiedShiftAndUserAvailability[];
}

const DragDropProvider: React.FC<DragDropProviderProps> = ({
  children,
  orders,
  shiftAndAvailabilities,
  events,
}: DragDropProviderProps) => {
  const { executeWithConflictCheck } = useAssignmentConflictHandler();
  const { t } = useTranslation();
  const { startDate, endDate } = useCalendar();
  const handleResourceDrop = useHandleResourceDrop();

  const { updateItem: updateOrder } = useOrders({ startDate, endDate });
  const { updateItem: updateEvent } = useEvents({ startDate, endDate });

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    let type = OrderPageTypes.EVENT;
    let event: any;

    const order = orders.find((x) => x.id === active.id);
    if (order) {
      type = OrderPageTypes.PRIVATE;
    } else {
      event = events.find((x) => x.id === active.id);
    }

    if (!over) {
      // Handle dropping outside any valid drop zone (unassign)
      if (!order && !event) {
        return;
      }
      if (order) {
        const updatedOrder = await executeWithConflictCheck(
          () => EmployeesService.assignOrderToEmployee({ order_id: order?.id }),
          t('calendar.assign_employee.successRemove'),
        );
        updateOrder(updatedOrder);
      } else if (event) {
        const result = await executeWithConflictCheck(
          () => EmployeesService.assignEventToEmployee({ event_id: event.id }),
          t('calendar.assign_employee.successRemove'),
        );
        updateEvent(result);
      }
      return;
    }

    // Parse the drop target using type-safe helper
    const dropTarget = parseDropTargetId(over.id);
    if (!dropTarget) {
      console.warn('Invalid drop target ID:', over.id);
      return;
    }

    if (isResourceDropTarget(dropTarget) || isUnassignedDropTarget(dropTarget)) {
      if (order) {
        await handleResourceDrop({
          order,
          dropTarget,
          updateOrder,
        });
        return;
      }
      if (event) {
        console.log('Dropped event on resource:', {
          eventId: event.id,
          dropTarget,
          eventData: event,
        });
        console.warn('Event resource assignment not yet implemented - API endpoint needed');
        return;
      }
    }

    // Handle employee drops
    if (isEmployeeDropTarget(dropTarget)) {
      const shiftAndAvailability = shiftAndAvailabilities.find((x) => x.id === dropTarget.shiftId);
      if ((!order && !event) || !shiftAndAvailability) {
        return;
      }

      if (type === OrderPageTypes.PRIVATE) {
        if (!order) {
          return;
        }
        const updatedOrder = await executeWithConflictCheck(
          () =>
            EmployeesService.assignOrderToEmployee({
              order_id: order?.id,
              employee_id: shiftAndAvailability?.user_id,
            }),
          t('calendar.assign_employee.success'),
        );
        updateOrder(updatedOrder);
      } else {
        if (!event) {
          return;
        }
        const updatedEvent = await executeWithConflictCheck(
          () =>
            EmployeesService.assignEventToEmployee({
              event_id: event.id,
              employee_id: shiftAndAvailability?.user_id,
            }),
          t('calendar.assign_employee.success'),
        );
        updateEvent(updatedEvent);
      }
    }
  };
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { delay: 450, tolerance: { x: 500 } } }),
    useSensor(TouchSensor, {
      // Press delay is required for touch devices to avoid drag-and-drop conflict with scrolling
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
  );
  return (
    <DndContext
      onDragEnd={handleDragEnd}
      collisionDetection={pointerWithin}
      sensors={sensors}
      modifiers={[restrictToHorizontalAxis]}>
      {children}
    </DndContext>
  );
};

export default DragDropProvider;
